// ============================================================
// Analytics v2 — AI traffic
//
//   AI assistants  people clicking through from ChatGPT, Perplexity, Claude…
//                  → channel `ai_assistant`, assistant name in utm_source
//   AI crawlers    bots fetching pages for training / search / live answers
//                  → never stored as events; counted as `_ai_crawl` rows
//                    that every normal report excludes (sql.ts baseWhere)
// ============================================================
/** Referrer host (or a parent of it) → assistant. */
export const AI_ASSISTANT_HOSTS = {
    'chatgpt.com': 'chatgpt',
    'chat.openai.com': 'chatgpt',
    'perplexity.ai': 'perplexity',
    'claude.ai': 'claude',
    'gemini.google.com': 'gemini',
    'bard.google.com': 'gemini',
    'copilot.microsoft.com': 'copilot',
    'edgeservices.bing.com': 'copilot',
    'you.com': 'you',
    'phind.com': 'phind',
    'poe.com': 'poe',
    'meta.ai': 'meta_ai',
    'grok.com': 'grok',
    'deepseek.com': 'deepseek',
    'chat.mistral.ai': 'mistral',
};
/** Extra utm_source spellings (besides the hosts and canonical names). */
const AI_SOURCE_ALIASES = {
    openai: 'chatgpt',
    'chat.openai': 'chatgpt',
    bard: 'gemini',
    'microsoft copilot': 'copilot',
    'bing copilot': 'copilot',
    metaai: 'meta_ai',
    'le chat': 'mistral',
    lechat: 'mistral',
};
const CANONICAL = new Set(Object.values(AI_ASSISTANT_HOSTS));
function assistantFromHost(host) {
    let h = host.toLowerCase().replace(/^www\./, '');
    // Walk up the labels: chat.deepseek.com → deepseek.com.
    while (h.includes('.')) {
        const hit = AI_ASSISTANT_HOSTS[h];
        if (hit) {
            return hit;
        }
        h = h.slice(h.indexOf('.') + 1);
    }
    return null;
}
/** Assistant named by a utm_source value ('chatgpt.com', 'perplexity', 'claude.ai', …). */
export function assistantFromSource(source) {
    const s = source.trim().toLowerCase();
    if (!s) {
        return null;
    }
    if (CANONICAL.has(s)) {
        return s;
    }
    return AI_SOURCE_ALIASES[s] ?? (s.includes('.') ? assistantFromHost(s) : null);
}
/** The AI assistant a visit came from (referrer host first, then utm_source), or null. */
export function classifyAiAssistant(input) {
    return ((input.referrer ? assistantFromHost(input.referrer) : null) ??
        assistantFromSource(input.utmSource));
}
// --- Crawlers ---
/** Internal event name for AI crawler hits. Not a valid client event name (leading '_'). */
export const AI_CRAWL_EVENT = '_ai_crawl';
/** Ordered: the first match wins (more specific tokens first). */
export const AI_CRAWLERS = [
    { name: 'ChatGPT-User', operator: 'openai', purpose: 'user' },
    { name: 'OAI-SearchBot', operator: 'openai', purpose: 'search' },
    { name: 'GPTBot', operator: 'openai', purpose: 'training' },
    { name: 'Claude-User', operator: 'anthropic', purpose: 'user' },
    { name: 'Claude-SearchBot', operator: 'anthropic', purpose: 'search' },
    { name: 'ClaudeBot', operator: 'anthropic', purpose: 'training' },
    { name: 'anthropic-ai', operator: 'anthropic', purpose: 'training' },
    { name: 'Perplexity-User', operator: 'perplexity', purpose: 'user' },
    { name: 'PerplexityBot', operator: 'perplexity', purpose: 'search' },
    { name: 'Google-Extended', operator: 'google', purpose: 'training' },
    { name: 'Applebot-Extended', operator: 'apple', purpose: 'training' },
    { name: 'Bytespider', operator: 'bytedance', purpose: 'training' },
    { name: 'CCBot', operator: 'common_crawl', purpose: 'training' },
    { name: 'Meta-ExternalAgent', operator: 'meta', purpose: 'training' },
    { name: 'Amazonbot', operator: 'amazon', purpose: 'search' },
    { name: 'cohere-ai', operator: 'cohere', purpose: 'training' },
    { name: 'DuckAssistBot', operator: 'duckduckgo', purpose: 'user' },
    { name: 'MistralAI-User', operator: 'mistral', purpose: 'user' },
];
const CRAWLER_RE = new RegExp(`(${AI_CRAWLERS.map(c => c.name.replace(/[-]/g, '\\-')).join('|')})`, 'i');
const BY_LOWER = new Map(AI_CRAWLERS.map(c => [c.name.toLowerCase(), c]));
/** Canonical crawler name for a User-Agent, or null when it is not a known AI crawler. */
export function classifyAiCrawler(ua) {
    if (!ua) {
        return null;
    }
    // Several tokens can appear in one UA; prefer the most specific (list order).
    const lower = ua.toLowerCase();
    if (!CRAWLER_RE.test(ua)) {
        return null;
    }
    for (const c of AI_CRAWLERS) {
        if (lower.includes(c.name.toLowerCase())) {
            return c.name;
        }
    }
    return null;
}
export function aiCrawlerInfo(name) {
    return BY_LOWER.get(name.toLowerCase()) ?? null;
}
/**
 * A crawler hit as a (minimal) v2 row: event `_ai_crawl`, label = crawler, d1 = operator,
 * d2 = purpose, d3 = ingest path; no session, visitor, geo or params. One low-cardinality
 * AE write per hit; every normal report excludes it by event name (sql.ts baseWhere).
 */
export function aiCrawlEvent(hit) {
    const info = aiCrawlerInfo(hit.crawler);
    return {
        id: crypto.randomUUID(),
        tenantId: hit.tenantId,
        projectId: hit.projectId,
        subTenant: hit.subTenant,
        event: AI_CRAWL_EVENT,
        ts: hit.ts,
        received: hit.ts,
        host: hit.host,
        path: hit.path,
        referrer: '',
        channel: 'direct',
        utm: { source: '', medium: '', campaign: '', term: '', content: '' },
        country: '',
        region: '',
        city: '',
        device: 'unknown',
        browser: '',
        os: '',
        session: '',
        visitor: '',
        userId: '',
        itemId: '',
        label: info?.name ?? hit.crawler,
        dims: [info?.operator ?? '', info?.purpose ?? '', hit.via, ''],
        value: 0,
        currency: '',
        qty: 0,
        durationMs: 0,
        metrics: [],
        consent: 'unknown',
        internal: false,
        source: hit.via === 'collect' ? 'client' : 'server',
        ctx: {},
        params: {},
    };
}
//# sourceMappingURL=ai.js.map